'use client'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useRouter } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { Globe } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useAlternateSlugsStore } from '@/lib/context/alternateSlugsContext'
import { Language } from '@/types/language.types'
import { useTranslations } from 'next-intl'

interface Props {
  currentLocale: string
  dark?: boolean
}

const LanguageSwitcher = ({ currentLocale, dark = false }: Props) => {
  const router = useRouter()
  const pathname = usePathname()
  const { alternateUrls } = useAlternateSlugsStore()
  const t = useTranslations()

  const handleLocaleChange = (locale: string) => {
    const isDevelopment = process.env.NODE_ENV === 'development'
    const targetLocale = locale as Language

    // Build the target path
    let targetPath: string

    if (alternateUrls && alternateUrls[targetLocale]) {
      // Use the pre-computed alternate URL for this language
      targetPath = alternateUrls[targetLocale]!
    } else {
      // No alternate URL - use current path (existing behavior)
      targetPath = pathname.replace(/^\/(en|no|se|dk)/, '')
    }

    if (isDevelopment) {
      router.replace(targetPath as any, { locale: targetLocale })
    } else {
      // Domain-based locale switching in preview/prod
      const hostMap: Record<string, string> = {
        en: 'https://www.firsthotels.com',
        no: 'https://www.firsthotels.no',
        se: 'https://www.firsthotels.se',
        dk: 'https://www.firsthotels.dk',
      }
      const host = hostMap[locale] || hostMap['en']

      window.location.href = `${host}${targetPath}`
    }
  }

  return (
    <Select value={currentLocale} onValueChange={handleLocaleChange}>
      <SelectTrigger
        aria-label={`${t('language')}: ${currentLocale.toUpperCase()}`}
        className={cn('max-w-fit rounded-full text-base', {
          'border-stone-200': dark,
          'border-stone-800': !dark,
        })}
      >
        <Globe
          className={cn('mr-1 size-5', {
            'text-stone-500': dark,
            'text-muted-foreground': !dark,
          })}
          strokeWidth={1.5}
        />
        <span
          className={cn('text-stone-200', {
            'text-stone-800': dark,
            'text-muted-foreground': !dark,
          })}
        >
          {currentLocale.toUpperCase()}
        </span>
      </SelectTrigger>
      <SelectContent className='z-[101]'>
        <SelectItem value='en'>English</SelectItem>
        <SelectItem value='no'>Norsk</SelectItem>
        <SelectItem value='se'>Svenska</SelectItem>
        <SelectItem value='dk'>Dansk</SelectItem>
      </SelectContent>
    </Select>
  )
}

export default LanguageSwitcher
